import { Station } from "../../types";
import { SWEDISH_STATIONS } from "../data/staticStations.ts";

const SR_BASE_URL =
  "https://api.sr.se/api/v2/channels?format=json&pagination=false";

export const getSverigesRadioStations = async (): Promise<Station[]> => {
  try {
    // 1. Fetch Live Data from SR API
    const response = await fetch(SR_BASE_URL);
    if (!response.ok) throw new Error("Failed to fetch SR");
    const data = await response.json();
    const channels = data.channels || [];

    // 2. Map API data to Station format
    interface SRChannel {
      channeltype: string;
      name: string;
      id: number;
      siteurl: string;
      image: string;
    }
    const apiStations: Station[] = channels.map((channel: SRChannel) => {
      const isNational = channel.channeltype === "Rikskanal";

      // Try to extract region from P4 names (e.g. "P4 Göteborg" -> "Göteborg")
      let region: string | undefined = undefined;

      if (!isNational && channel.name.startsWith("P4")) {
        const parts = channel.name.split("P4");
        if (parts.length > 1) {
          region = parts[1].trim();
        }
      } else if (isNational) {
        region = "National";
      }

      return {
        id: `sr-${channel.id}`,
        name: channel.name,
        country: "Sweden",
        countryCode: "SE",
        state: region, // "National" for P1/P2/P3, City name for P4, or undefined
        provider: "sr",
        // FIX: Use http-live.sr.se for direct streams to avoid CORS issues
        // Note: For P1 use channel ID 132, P2 is 2562, P3 is 164, P4 regional varies
        streamUrl: `https://http-live.sr.se/srapi/${channel.id}-mp3-192`,
        website: channel.siteurl,
        logoUrl: channel.image,
        bitrate: 128, // API usually gives 128k mp3/aac
        codec: "MP3",
        language: ["Swedish"],
        tags: ["public-service", "sweden", isNational ? "national" : "local"],
        votes: isNational ? 5000 : 500,
        clickTrend: isNational ? 50 : 10,
        onlineStatus: "online",
      } as Station;
    });

    // 3. Merge with Curated Static Stations (High Quality Overrides)
    // We prioritize our static definitions because they have hand-picked high-bitrate AAC streams
    // and verified metadata.

    const mergedStations = [...apiStations];

    // Merge with curated Swedish static stations (commercial stations like RIX FM, Mix Megapol, etc.)
    SWEDISH_STATIONS.forEach((staticStation) => {
      // Find if this station exists in the API list (by name approximation)
      const index = mergedStations.findIndex(
        (s) =>
          s.name.toLowerCase().includes(staticStation.name.toLowerCase()) ||
          staticStation.name.toLowerCase().includes(s.name.toLowerCase()),
      );

      if (index !== -1) {
        // OVERRIDE: Use our curated data but keep dynamic IDs and LOGOS from API
        // This ensures we get the latest images from SR while keeping our curated tags/streams.
        mergedStations[index] = {
          ...staticStation,
          id: mergedStations[index].id,
          logoUrl: mergedStations[index].logoUrl || staticStation.logoUrl, // PREFER API LOGO
        };
      } else {
        // Station not in SR API (commercial stations) - add it with our curated logo
        mergedStations.push(staticStation);
      }
    });

    return mergedStations;
  } catch (error) {
    console.error("SR Provider Error:", error);
    // Fallback to just static stations if API fails
    return SWEDISH_STATIONS;
  }
};
